<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { buildPendingRecordTitle } from '@/entities/GeneratedRecord';
  import { ColoredButton, FormInput, IconImage } from '@/shared/ui';
  import CloseIcon from '@/shared/assets/icons/CloseIcon.svg';
  import { RECORD_INDEXER_MAX_LENGTH } from '../model/record-indexing';

  const props = withDefaults(
    defineProps<{
      prefix: string;
      initialValue: string;
      serverError: string | null;
      submitting?: boolean;
    }>(),
    { submitting: false }
  );

  const emit = defineEmits<{
    cancel: [];
    create: [indexer: string];
    edit: [];
  }>();

  const REQUIRED_MESSAGE = 'Informe o indexador do registro.';
  const INPUT_ID = 'record-indexer';

  const indexer = ref(props.initialValue);
  const touched = ref(false);

  const isEmpty = computed(() => indexer.value.trim() === '');
  const titlePreview = computed(() => buildPendingRecordTitle(props.prefix, indexer.value));
  const error = computed(() => {
    if (props.serverError) return props.serverError;
    return touched.value && isEmpty.value ? REQUIRED_MESSAGE : '';
  });

  function setIndexer(value: string): void {
    indexer.value = value;
    touched.value = true;
    if (props.serverError) emit('edit');
  }

  function submit(): void {
    touched.value = true;
    if (isEmpty.value || props.serverError || props.submitting) return;
    emit('create', indexer.value.trim());
  }
</script>

<template>
  <VueFinalModal
    :model-value="true"
    class="flex items-center justify-center"
    content-class="w-[95vw] max-w-[400px] rounded-2xl border border-gray-02 bg-white-01"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    :click-to-close="false"
    :esc-to-close="false"
  >
    <div
      class="flex flex-col"
      data-testid="record-indexer-modal"
    >
      <div class="flex h-12 justify-end p-3">
        <button
          type="button"
          class="text-gray-03"
          aria-label="Fechar"
          data-testid="record-indexer-close"
          @click="emit('cancel')"
        >
          <IconImage
            :src="CloseIcon"
            alt=""
          />
        </button>
      </div>

      <form
        class="flex flex-col gap-8 px-6 pb-12"
        novalidate
        @submit.prevent="submit"
      >
        <h2
          class="text-center text-sm font-semibold text-gray-07"
          data-testid="record-indexer-title"
        >
          Indexador do registro
        </h2>

        <div class="flex flex-col gap-1.5">
          <FormInput
            :id="INPUT_ID"
            type="text"
            label="Indexador:"
            label-class="text-sm text-gray-07"
            placeholder="Insira o indexador aqui"
            :max-length="RECORD_INDEXER_MAX_LENGTH"
            input-class="!h-11 !rounded-lg !py-0 bg-white-01 text-sm text-gray-07 shadow-xs placeholder:font-light placeholder:text-gray-03"
            :model-value="indexer"
            :error="error || undefined"
            @update:model-value="setIndexer"
            @blur="touched = true"
          />
          <p
            v-if="titlePreview"
            class="text-[13px]/[18px] text-gray-03 [overflow-wrap:anywhere]"
            data-testid="record-indexer-title-preview"
          >
            Título: {{ titlePreview }}
          </p>
          <!-- FormInput draws its error without a live region, so screen readers hear it only from here. -->
          <p
            class="sr-only"
            role="alert"
            data-testid="record-indexer-error-announcement"
          >
            {{ error }}
          </p>
        </div>

        <div class="flex justify-center gap-3">
          <ColoredButton
            outlined
            color="gray-03"
            class="flex-1 justify-center !text-sm"
            text="Cancelar"
            data-testid="record-indexer-cancel"
            @click="emit('cancel')"
          />
          <ColoredButton
            type="submit"
            class="flex-1 justify-center !text-sm"
            text="Criar"
            :disabled="isEmpty || Boolean(serverError) || submitting"
            data-testid="record-indexer-submit"
          />
        </div>
      </form>
    </div>
  </VueFinalModal>
</template>
